// HybridScrollManager.jsx
import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";

const HybridScrollManager = () => {
  const location = useLocation();
  const scrollPositions = useRef(new Map());
  const isPopstateNavigation = useRef(false);
  const saveTimeoutRef = useRef(null);

  // Save scroll position with debouncing
  const saveScrollPosition = (path, immediate = false) => {
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
    }

    const doSave = () => {
      const scrollY = window.scrollY;
      const scrollX = window.scrollX;
      
      // Only save if there's actual scroll position
      if (scrollY > 0 || scrollX > 0) {
        scrollPositions.current.set(path, {
          x: scrollX,
          y: scrollY,
          timestamp: Date.now()
        });
        console.log(`💾 Saved scroll position for ${path}:`, scrollY);
      }
    };

    if (immediate) {
      doSave();
    } else {
      saveTimeoutRef.current = setTimeout(doSave, 100);
    }
  };

  useEffect(() => {
    const currentPath = location.pathname;

    // Determine scroll behavior
    setTimeout(() => {
      if (isPopstateNavigation.current) {
        // BROWSER BACK/FORWARD: Restore scroll position
        const savedPosition = scrollPositions.current.get(currentPath);
        if (savedPosition) {
          window.scrollTo({
            left: savedPosition.x,
            top: savedPosition.y,
            behavior: 'auto'
          });
          console.log(`🔙 Restored scroll position for ${currentPath}:`, savedPosition.y);
        } else {
          window.scrollTo(0, 0);
          console.log(`🔙 No saved position for ${currentPath}, scrolled to top`);
        }
        isPopstateNavigation.current = false;
      } else {
        // PROGRAMMATIC NAVIGATION: Scroll to top
        window.scrollTo(0, 0);
        console.log(`➡️ New page navigation to ${currentPath}, scrolled to top`);
      }
    }, 50);

  }, [location.pathname]);

  // Save scroll position when user scrolls
  useEffect(() => {
    let scrollTimeout;
    
    const handleScroll = () => {
      if (scrollTimeout) {
        clearTimeout(scrollTimeout);
      }
      
      scrollTimeout = setTimeout(() => {
        saveScrollPosition(location.pathname);
      }, 150); // Debounce scroll saving
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    
    return () => {
      window.removeEventListener('scroll', handleScroll);
      if (scrollTimeout) {
        clearTimeout(scrollTimeout);
      }
    };
  }, [location.pathname]);

  // Browser back/forward button detection
  useEffect(() => {
    const handlePopState = (event) => {
      console.log('🌐 Browser back/forward detected');
      // Save current position immediately before navigation
      saveScrollPosition(location.pathname, true);
      isPopstateNavigation.current = true;
    };

    window.addEventListener('popstate', handlePopState);
    
    // Save scroll position before page unload
    const handleBeforeUnload = () => {
      saveScrollPosition(location.pathname, true);
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('popstate', handlePopState);
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [location.pathname]);

  // Save scroll position when tab becomes hidden or when clicking links
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.hidden) {
        saveScrollPosition(location.pathname, true);
      }
    };

    // Save position when user clicks on links
    const handleClick = (event) => {
      // Check if it's a navigation link
      const link = event.target.closest('a');
      if (link && link.href && !link.href.startsWith('mailto:') && !link.href.startsWith('tel:')) {
        saveScrollPosition(location.pathname, true);
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    document.addEventListener('click', handleClick, true);
    
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      document.removeEventListener('click', handleClick, true);
    };
  }, [location.pathname]);

  // Cleanup old scroll positions periodically
  useEffect(() => {
    const cleanup = () => {
      const now = Date.now();
      const maxAge = 30 * 60 * 1000; // 30 minutes
      
      for (const [path, data] of scrollPositions.current.entries()) {
        if (now - data.timestamp > maxAge) {
          scrollPositions.current.delete(path);
        }
      }
    };

    const interval = setInterval(cleanup, 10 * 60 * 1000); // Every 10 minutes
    return () => clearInterval(interval);
  }, []);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
    };
  }, []);

  return null;
};

export default HybridScrollManager;